import { sortAsc } from '@strata/helper-functions';
import { Button } from '@strata/react-buttons';
import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { WarningToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { FormExceptionDialog } from 'components/referralForm/dialogs/FormExceptionDialog';
import { FieldFAQ } from 'components/referralForm/FieldFAQ';
import { useFormResetRef, useInputFocusRef } from 'components/referralForm/FormHelpers';
import { fetchPolicyListForPayer } from 'store/referralForm/clientSlice';
import { fetchDiagnoses, fetchMedications } from 'store/referralForm/referenceSlice';
import { nextSection, updateCapturedData } from 'store/referralForm/referralFormSlice';

export const MedicationAndDiagnosis = ({ section, isOpen, width }) => {
    const dispatch = useDispatch();
    const { capturedData, activeSection } = useSelector((state) => state.referralForm);

    // Store values to submit for clinical exception
    const [valuesToSubmit, setValuesToSubmit] = useState();

    const { pendingReauth, segmentId } = capturedData;
    const medicationRef = useInputFocusRef(isOpen && !pendingReauth, activeSection, section);
    const formRef = useFormResetRef(isOpen);

    // Focus submit button if pending reauth
    const submitBtnRef = useRef(null);
    useEffect(() => {
        if (pendingReauth && isOpen && activeSection === section) {
            submitBtnRef.current?.focus();
        }
    }, [pendingReauth, isOpen, activeSection, section]);

    const { diagnosisList, medicationList } = useSelector((state) => state.reference);
    useEffect(() => {
        dispatch(fetchMedications(segmentId));
        if (!diagnosisList) {
            dispatch(fetchDiagnoses());
        }
    }, [diagnosisList, segmentId, dispatch]);

    const { payerId, prescriberNpi } = capturedData;
    const policyList = useSelector((state) => state.client.policyListMap[payerId]);
    useEffect(() => {
        if (payerId && prescriberNpi && !policyList) {
            dispatch(fetchPolicyListForPayer(payerId, prescriberNpi));
        }
    }, [policyList, payerId, prescriberNpi, dispatch]);

    const medicationValues = (policyList ? Object.keys(policyList) : [])
        .filter((productServiceOid) => medicationList?.[productServiceOid]) // ensure oid exists in medications list
        .map((productServiceOid) => ({
            oid: productServiceOid,
            name: medicationList?.[productServiceOid].description,
        }))
        .sort((a, b) => sortAsc(a.name, b.name));

    const onSubmit = (values, diagnosisException) => {
        if (diagnosisException) {
            WarningToast({ message: 'Clinical exception will be sent for review.' });
        }
        dispatch(updateCapturedData({ ...values, diagnosisException }));
        dispatch(nextSection(section, !!diagnosisException));
    };

    return (
        <>
            <FormExceptionDialog
                exceptionType={FormExceptionDialog.ExceptionTypes.DIAGNOSIS}
                isOpen={!!valuesToSubmit}
                onSubmit={(exception) => onSubmit(valuesToSubmit, exception)}
                onSecondaryButtonClick={() => setValuesToSubmit()}
                diagnosisList={diagnosisList}
            />
            <Formik
                initialValues={capturedData}
                enableReinitialize
                validationSchema={Yup.object({
                    productServiceOid: Yup.string().required(
                        validationStrings.requiredField.errorMessage,
                    ),
                    diagnosisCode: Yup.string().when('diagnosisNotListed', {
                        is: (diagnosisNotListed) => diagnosisNotListed.length === 0,
                        then: (schema) =>
                            schema.required(validationStrings.requiredField.errorMessage),
                    }),
                    diagnosisNotListed: Yup.array(),
                })}
                onSubmit={(values, { setSubmitting }) => {
                    if (values.diagnosisNotListed.length > 0) {
                        setValuesToSubmit(values);
                    } else {
                        onSubmit(values);
                    }
                    setSubmitting(false);
                }}
                innerRef={formRef}
            >
                {(formik) => {
                    const diagnosisValues = (diagnosisList ?? [])
                        // ensure oid exists in diagnoses list
                        .filter((diagnosis) =>
                            (policyList?.[formik.values.productServiceOid] ?? []).includes(
                                diagnosis.oid,
                            ),
                        );

                    return (
                        <Form autoComplete="off">
                            <div style={{ display: 'flex' }}>
                                <div style={{ width }} className="margin__right--medium">
                                    <SuggestSingleSelectFormik
                                        id="productServiceOid"
                                        label="Medication"
                                        required
                                        isLoading={!(medicationList && policyList)}
                                        values={medicationValues}
                                        inputRef={medicationRef}
                                        onChange={(newValue) =>
                                            formik.setValues(
                                                {
                                                    ...formik.values,
                                                    productServiceOid: newValue?.oid,
                                                    medication: newValue?.name,
                                                    // Clear out diagnosis selection since options will change
                                                    diagnosisCode: undefined,
                                                    diagnosisOid: undefined,
                                                },
                                                false, // CORE-1246: validation could become out of sync when selecting med w/ only one dx
                                            )
                                        }
                                        selectedItem={medicationValues.find(
                                            (value) =>
                                                value.oid === formik.values.productServiceOid,
                                        )}
                                    />
                                </div>
                                <FieldFAQ
                                    faqText="Can't find a medication?"
                                    tooltipText={
                                        <div style={{ width: 400 }}>
                                            If the medication you are looking to prescribe is not
                                            listed, it is not managed through the Free Market Health
                                            Marketplace for the health plan.
                                        </div>
                                    }
                                />
                            </div>
                            <div style={{ display: 'flex' }}>
                                <div style={{ width }} className="margin__right--medium">
                                    <SuggestSingleSelectFormik
                                        id="diagnosisCode"
                                        label="Diagnosis"
                                        required={formik.values.diagnosisNotListed.length === 0}
                                        disabled={formik.values.diagnosisNotListed.length > 0}
                                        isLoading={!(diagnosisList && policyList)}
                                        values={diagnosisValues}
                                        onChange={(newValue) =>
                                            formik.setValues({
                                                ...formik.values,
                                                diagnosisCode: newValue?.secondaryData,
                                                diagnosisOid: newValue?.oid,
                                            })
                                        }
                                        selectedItem={diagnosisValues.find(
                                            (value) =>
                                                value.secondaryData === formik.values.diagnosisCode,
                                        )}
                                    />
                                </div>
                                <FieldFAQ
                                    faqText="Can't find a diagnosis?"
                                    tooltipText={
                                        <div style={{ width: 400 }}>
                                            <p>
                                                If the diagnosis code that you are looking for is
                                                not listed, it is not approved based on clinical
                                                policy for this medication.
                                            </p>
                                            <p>You can continue with an exception.</p>
                                            <p>
                                                If an exception is approved, a referral will
                                                automatically be sent to Free Market Health and no
                                                additional work will be required.
                                            </p>
                                        </div>
                                    }
                                />
                            </div>
                            <CheckboxListFormik
                                name="diagnosisNotListed"
                                options={['Diagnosis is not listed.']}
                                onChange={(newValues) =>
                                    formik.setValues({
                                        ...formik.values,
                                        diagnosisNotListed: newValues,
                                        diagnosisCode: undefined,
                                        diagnosisOid: undefined,
                                    })
                                }
                            />
                            <Button
                                intent={Button.Intent.PRIMARY}
                                label="Continue"
                                type="submit"
                                ref={submitBtnRef}
                                disabled={!(formik.isValid && (formik.dirty || pendingReauth))}
                            />
                        </Form>
                    );
                }}
            </Formik>
        </>
    );
};

MedicationAndDiagnosis.propTypes = {
    section: PropTypes.number.isRequired,
    isOpen: PropTypes.bool.isRequired,
    width: PropTypes.string.isRequired,
};
